/**
 * Zunelo Static UI Display Configuration
 *
 * Pure, static, presentation-only configuration: workflow step ordering used to
 * render Jira transition selectors, static badge/count placeholders, and the
 * daily standup send-time/channel label. None of this is backend-sourced
 * domain data, so per AGENTS.md section 4.3 it is the one narrow exception
 * allowed to live outside src/services/clients/ and be imported directly by
 * UI components / services.
 *
 * Filter-pill predicates (previously `BADGES_CONFIG` / `AGENT_FILTERS` here)
 * now live in `apps/web/src/domain/filtering.ts` as the single source of
 * truth shared by pill-bar counts and view filtering.
 */

import { Tone } from '../domain/types';

/**
 * Standard Jira workflow step ordering, used to populate the status transition
 * dropdown in JiraCard when a ticket sits in a non-standard/custom state.
 */
export const FLOW_STEPS: string[] = [
  'Tareas por hacer',
  'Planificación',
  'IN DEV',
  'En curso',
  'Pull request',
  'Ready for QA',
  'In QA',
  'Done',
  'On Hold',
];

export interface BadgeFilter {
  id: string;
  label: string;
  count?: number;
  tone?: Tone;
}

/** Static badge/count placeholders shown in filter bars before live counts are known. */
export const BADGES: Record<'panel' | 'jira' | 'slack' | 'github' | 'agents', BadgeFilter[]> = {
  panel: [
    { id: 'all', label: 'Todo', count: 20 },
    { id: 'you', label: 'Tu turno', count: 8, tone: 'bad' },
    { id: 'them', label: 'Esperando', count: 6, tone: 'flat' },
    { id: 'blocked', label: 'Bloqueado', count: 3, tone: 'warn' },
    { id: 'ready', label: 'Listo', count: 3, tone: 'ok' },
  ],
  jira: [
    { id: 'all', label: 'Todos', count: 6 },
    { id: 'bugs', label: 'Bugs', count: 2, tone: 'bad' },
    { id: 'stories', label: 'Historias', count: 2, tone: 'ok' },
    { id: 'tasks', label: 'Tareas', count: 2, tone: 'accent' },
    { id: 'in-dev', label: 'En Dev', count: 2, tone: 'accent' },
    { id: 'hold', label: 'On Hold', count: 2, tone: 'warn' },
  ],
  slack: [
    { id: 'all', label: 'Todos', count: 4 },
    { id: 'unread', label: 'No leídos', count: 2, tone: 'bad' },
    { id: 'mentions', label: 'Menciones', count: 2, tone: 'accent' },
  ],
  github: [
    { id: 'all', label: 'Todos', count: 7 },
    { id: 'author', label: 'Mis PRs', count: 6, tone: 'accent' },
    { id: 'reviewer', label: 'Por revisar', count: 1, tone: 'bad' },
    { id: 'ready-to-merge', label: 'Listos para merge', count: 2, tone: 'ok' },
    { id: 'changes-requested', label: 'Cambios pedidos', count: 1, tone: 'bad' },
  ],
  agents: [
    { id: 'all', label: 'Todos', count: 3 },
    { id: 'running', label: 'Corriendo', count: 2, tone: 'ok' },
    { id: 'paused', label: 'Pausados', count: 1, tone: 'warn' },
  ],
};

/** Static display config for the daily standup widget's send time/channel label. */
export const DAILY_CONFIG_DATA = { at: '09:15', channel: '#daily-standup' };
